import { DSButton } from "@components/design-system/molecules";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { useLiveQuery } from "dexie-react-hooks";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { db } from "../../db";
import { useNoteCreation, useSyncNotes } from "../../hooks";
import { useTeam } from "../../hooks/use-team";
import { DSNoteItem } from "../design-system/organisms/DSNoteItem";
import NoteCreatedAt from "../notebook/note/NoteCreatedAt";

export default function UnsyncedRecordingsList({
  showSyncAll = false,
}) {
  const navigate = useNavigate();

  // Tick every 5s so the time-based filter re-evaluates for recent recordings.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const unsyncedRecordings = useLiveQuery(async () => {
    const threshold = moment().subtract(15, "seconds").format();
    const items = await db.unsyncedRecordings
      .filter((r) => !r.is_synced && r.saved_date < threshold)
      .sortBy("saved_date");
    return items.reverse();
  }, [tick]);

  const { addToSyncNotesQueue, syncNotesQueue, uploadIndex } =
    useSyncNotes();
  const { currentTeam } = useTeam();
  const createNote = useNoteCreation({ variant: "quick" });

  const createNewNote = useCallback(
    (
      audioStorageLink,
      storageObjectName,
      jobType,
      _isSyncedRecording, // required by uploadToCloud call signature
      _isNoteFromFileUpload, // required by uploadToCloud call signature
      recordingAs,
      title,
      recordingConsentEventUuid,
    ) =>
      createNote({
        audioStorageLink,
        storageObjectName,
        jobType,
        title,
        recordingAs,
        teamUuid: currentTeam?.uuid,
        recordingConsentEventUuid,
      }),
    [createNote, currentTeam?.uuid],
  );

  const handleSyncAll = () => {
    unsyncedRecordings
      ?.filter(
        (item) =>
          !item.is_synced &&
          !syncNotesQueue.some((q) => q.id === item.id),
      )
      .forEach((item) => {
        mixpanel.track("Synced Recording From Inbox", {
          recordingId: item.id,
          jobType: item.jobType,
          source: "sync_all",
        });
        addToSyncNotesQueue(item, createNewNote);
      });
  };

  if (!unsyncedRecordings?.length) return null;

  return (
    <>
      {showSyncAll && (
        <div className="flex justify-end mx-sm mt-sm">
          <DSButton
            variant="primary"
            size="sm"
            icon={faSyncAlt}
            onClick={handleSyncAll}
            dataTestId="sync-all-recordings"
          >
            Sync All
          </DSButton>
        </div>
      )}
      {unsyncedRecordings.map((item) => {
        const isSyncing = uploadIndex === item.id;
        const isQueued = syncNotesQueue.some((q) => q.id === item.id);
        return (
          <div key={`unsynced-${item.id}`} className="my-md mx-sm">
            <DSNoteItem
              variant="unsynced"
              status={isSyncing || isQueued ? "syncing" : "unsynced"}
              onClick={() => navigate(`/unsynced_note/${item.id}`)}
              href={`/unsynced_note/${item.id}`}
              onSyncClick={() => {
                if (isSyncing || isQueued) return;
                mixpanel.track("Synced Recording From Inbox", {
                  recordingId: item.id,
                  jobType: item.jobType,
                });
                addToSyncNotesQueue(item, createNewNote);
              }}
              dataTestId={`unsynced-recording-${item.id}`}
              subtitle={
                <NoteCreatedAt
                  createdAt={item.saved_date}
                  size="sm"
                />
              }
              hideTitle={true}
            />
          </div>
        );
      })}
    </>
  );
}
